import { useQuery } from "../data/useQuery.js";
import { useBoundQuery, FillPrompt } from "../data/DwSql.js";
import { ErrorBadge } from "./ErrorBadge.js";
import { fmtCompact, fmtCurrency } from "./format.js";
import type { QueryResult } from "../../shared/types.js";
import type { VizProps } from "./registry.js";

// Signed profit bars (e.g. net profit by month): green for >= 0, red for < 0,
// each labelled to the nearest $1k, with a zero reference line. First column
// (or x=) is the category; the value is the next numeric column (or value=).
const GREEN = "#22c55e";
const RED = "#ef4444";

export function WaterfallViz({ source, attrs }: VizProps) {
  const { sql, values, path, status, missing } = useBoundQuery(source.trim());
  const q = useQuery(status === "ready" ? sql : null, {
    // Explicit attr overrides the query's @maxAge pragma; absent → pragma →
    // forever (useQuery sends maxAgeDefault).
    ttl: attrs.maxAge ?? attrs.ttl,
    values,
    path,
  });
  return (
    <div className="dw-embed dw-embed-chart">
      {status === "blocked" && <FillPrompt names={missing} />}
      {status === "ready" && q.status === "loading" && (
        <div className="dw-loading">Running…</div>
      )}
      {status === "ready" && q.status === "error" && (
        <ErrorBadge message={q.error.message} />
      )}
      {status === "ready" && q.status === "ready" && (
        <Waterfall result={q.data} attrs={attrs} />
      )}
    </div>
  );
}

/** Nearest $1,000: 19857 -> "$20K", -31527 -> "-$32K". */
function nearest1k(v: number): string {
  const k = Math.round(v / 1000);
  return `${k < 0 ? "-" : ""}$${Math.abs(k)}K`;
}

function Waterfall({
  result,
  attrs,
}: {
  result: QueryResult;
  attrs: Record<string, string>;
}) {
  const colNames = result.columns.map((c) => c.name);
  const xKey = attrs.x && colNames.includes(attrs.x) ? attrs.x : colNames[0];
  const xi = colNames.indexOf(xKey);
  const valueCol =
    attrs.value && colNames.includes(attrs.value)
      ? attrs.value
      : colNames.find((c) => c !== xKey) ?? colNames[0];
  const vi = colNames.indexOf(valueCol);

  const rows = result.rows.map((r) => ({
    cat: String(r[xi] ?? ""),
    v: Number(r[vi] ?? 0) || 0,
  }));
  if (rows.length === 0) return <div className="dw-empty">No rows.</div>;

  const vals = rows.map((r) => r.v);
  const dataMin = Math.min(0, ...vals);
  const dataMax = Math.max(0, ...vals);
  const pad = Math.max((dataMax - dataMin) * 0.18, 1);
  const domMin = dataMin - pad;
  const domMax = dataMax + pad;

  const W = Math.max(640, rows.length * 64);
  const H = Number(attrs.height) || 320;
  const m = { top: 16, right: 16, bottom: 36, left: 56 };
  const plotW = W - m.left - m.right;
  const plotH = H - m.top - m.bottom;
  const y = (v: number) =>
    m.top + (plotH * (domMax - v)) / (domMax - domMin);
  const zeroY = y(0);
  const groupW = (plotW / rows.length) * 0.6;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => domMin + t * (domMax - domMin));

  return (
    <div className="dw-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none"
        style={{ height: H }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={m.left} x2={W - m.right} y1={y(t)} y2={y(t)}
              stroke="var(--dw-border)" strokeDasharray="3 3" />
            <text x={m.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11"
              fill="var(--dw-muted)">
              {fmtCompact(t)}
            </text>
          </g>
        ))}
        {/* zero reference line */}
        <line x1={m.left} x2={W - m.right} y1={zeroY} y2={zeroY}
          stroke="var(--dw-muted)" />
        {rows.map((r, i) => {
          const cx = m.left + (i + 0.5) * (plotW / rows.length);
          const bx = cx - groupW / 2;
          const top = r.v >= 0 ? y(r.v) : zeroY;
          const bh = Math.abs(y(r.v) - zeroY);
          return (
            <g key={i}>
              <rect x={bx} y={top} width={groupW} height={bh} rx={2}
                fill={r.v >= 0 ? GREEN : RED}>
                <title>{`${r.cat}: ${fmtCurrency(r.v)}`}</title>
              </rect>
              <text x={cx} y={r.v >= 0 ? y(r.v) - 4 : y(r.v) + 12}
                textAnchor="middle" fontSize="10"
                fill={r.v >= 0 ? GREEN : RED}>
                {nearest1k(r.v)}
              </text>
              <text x={cx} y={H - m.bottom + 16} textAnchor="middle"
                fontSize="11" fill="var(--dw-muted)">
                {r.cat}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
